import { observer } from 'mobx-react-lite'
import { Dialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { BreakoutTabButton } from 'components/design-system/BreakoutTabButton'
import { useState, useEffect } from 'react'
import { useTranslationTyped } from 'i18n/i18n'
import type { StudentPeerReviewController } from '@breakoutlearning/firebase-repository/cubits/StudentAssignment/StudentPeerReviewController'
import { PeerReviewFeedbackForm } from 'components/PeerReview/PeerReviewFeedbackForm'
import { PeerReviewInstructionsTab } from 'components/PeerReview/PeerReviewInstructionsTab'
import { Spinner } from 'components/Spinner'
import { DialogTopSectionWithIcon } from 'components/dialogs/DialogTopSectionWithIcon'
import classNames from 'classnames'
import { useDialogs } from 'hooks/dialogs'
import { toast } from 'react-hot-toast'
import type { PeerReviewMeaningfulScore } from '@breakoutlearning/firebase-repository/models/RoomStateUPRPeerReview'

type PeerReviewCompletionDialogTab = 'instructions' | 'feedbackForm'

export const PeerReviewCompletionDialog = observer(
  function PeerReviewCompletionDialog({
    controller,
    assignmentTitle,
  }: {
    controller: StudentPeerReviewController
    assignmentTitle: string
  }) {
    const { tt } = useTranslationTyped()
    const { popDialog } = useDialogs()
    const [selectedTab, setSelectedTab] =
      useState<PeerReviewCompletionDialogTab>('instructions')
    const [stagedReviews, setStagedReviews] = useState<
      Map<string, Map<string, PeerReviewMeaningfulScore>>
    >(new Map())
    const [stagedNotes, setStagedNotes] = useState<Map<string, string>>(
      new Map()
    )
    const [stagedSharing, setStagedSharing] = useState<Map<string, boolean>>(
      new Map()
    )

    useEffect(() => {
      controller.initializeAsReviewer()
    }, [controller])

    const peerReviewsEnabled = controller.peerReviewsEnabled
    const peerReviewQuestions = controller.peerReviewQuestions
    const reviewsByReviewee = controller.reviewsOfUserByReviewee

    const isLoading =
      peerReviewsEnabled.isLoading ||
      peerReviewQuestions.isLoading ||
      reviewsByReviewee.isLoading

    if (isLoading) {
      return (
        <Dialog
          aria-labelledby={undefined}
          size="lg"
          dismissOnEscapeKey
          className="flex flex-col"
        >
          <DialogCloseButton />
          <div className="flex h-96 items-center justify-center">
            <Spinner />
          </div>
        </Dialog>
      )
    }

    if (!peerReviewsEnabled.value) {
      return null
    }

    return (
      <Dialog
        aria-labelledby={undefined}
        size="lg"
        className="flex max-w-[min(840px,90%)] flex-col"
        innerClassName="flex flex-col p-13"
        ignoreBackdropClick
        dismissOnEscapeKey
      >
        <DialogCloseButton />
        <DialogTopSectionWithIcon
          className="!mb-2"
          title={tt.student_assignment.peer_review_dialog.title({
            title: assignmentTitle,
          })}
        />
        <div
          role="tablist"
          className="mb-3 flex w-full border-b-2 border-outline-light"
        >
          <BreakoutTabButton
            isSelected={selectedTab === 'instructions'}
            onClick={() => setSelectedTab('instructions')}
            className="w-full !rounded-none"
          >
            {tt.student_assignment.peer_review_dialog.instructions()}
          </BreakoutTabButton>
          <BreakoutTabButton
            isSelected={selectedTab === 'feedbackForm'}
            onClick={() => setSelectedTab('feedbackForm')}
            className="w-full !rounded-none"
          >
            {tt.student_assignment.peer_review_dialog.feedback_form()}
          </BreakoutTabButton>
        </div>

        <div className="grid min-h-[28rem] flex-1 grid-rows-1">
          <PeerReviewInstructionsTab
            className={classNames(
              'col-start-1 row-start-1',
              selectedTab !== 'instructions' && 'invisible'
            )}
            peerReviewQuestions={peerReviewQuestions.models}
            setSelectedTab={setSelectedTab}
          />

          {reviewsByReviewee.value && (
            <PeerReviewFeedbackForm
              className={classNames(
                'col-start-1 row-start-1',
                selectedTab !== 'feedbackForm' && 'invisible'
              )}
              peerReviewQuestions={peerReviewQuestions.models}
              reviewsOfUserByReviewee={reviewsByReviewee.value}
              controller={controller}
              onSaveComplete={() => {
                toast.success(
                  tt.student_assignment.peer_review_dialog.peer_review_completed()
                )
                popDialog()
              }}
              stagedReviews={stagedReviews}
              setStagedReviews={setStagedReviews}
              stagedNotes={stagedNotes}
              setStagedNotes={setStagedNotes}
              stagedSharing={stagedSharing}
              setStagedSharing={setStagedSharing}
            />
          )}
        </div>
      </Dialog>
    )
  }
)
